import { useCallback } from "react";

import { Trans } from "@lingui/react/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  NOTIFICATIONS_VALID_TABS,
  NotificationsView,
  type NotificationsTab,
} from "@/components/notifications/notifications-view";

interface NotificationsSearch {
  tab: NotificationsTab;
}

export const Route = createFileRoute("/_authenticated/account/business/$businessId/notifications")({
  validateSearch: (search: Record<string, unknown>): NotificationsSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    return {
      tab: NOTIFICATIONS_VALID_TABS.includes(raw as NotificationsTab)
        ? (raw as NotificationsTab)
        : "all",
    };
  },
  component: BusinessNotificationsPage,
});

function BusinessNotificationsPage() {
  const { businessId } = Route.useParams();
  const { tab: activeTab } = Route.useSearch();
  const navigate = useNavigate();

  const setActiveTab = useCallback(
    (key: NotificationsTab) =>
      navigate({
        to: "/account/business/$businessId/notifications",
        params: { businessId },
        search: { tab: key },
      }),
    [navigate, businessId],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Notifications</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Business workspace updates: subscription seats, account changes, alerts.</Trans>
        </p>
      </header>
      <NotificationsView
        workspaceType="business"
        workspaceId={businessId}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <AccountSecurityBanner />
    </div>
  );
}
